/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Display
 * Reason: Read-only processing panel of one pd.PdPartner in its object view („Übersicht“): the core
 *         processing lock (Verarbeitungsstatus, Art, gesperrt seit, sperrender Joblauf), the delta watermark and
 *         the partner's last collection and export run, each with status and a link into the generic object view.
 */
import {Component, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {PdPartnerDto} from '../../../model/dto/entity/pd-partner.dto';
import {PdPartnerCollectionRunDto} from '../../../model/dto/entity/pd-partner-collection-run.dto';
import {PdPartnerExportRunDto} from '../../../model/dto/entity/pd-partner-export-run.dto';
import {PdRunStatusEnum} from '../../../model/dto/enum/pd-run-status.enum';
import {
    pdCollectionModeLabel,
    pdExportChannelLabel,
    pdFormatDateTime,
    pdIsLocked,
    pdProcessingKindLabel,
    pdProcessingStatusLabel,
    pdProcessingStatusTone,
    pdRunStatusLabel,
    pdRunStatusTone,
    pdStatusChipClass,
    PdStatusTone
} from '../../../logic/pd-status-display';

/** Load state of one „letzter Lauf“ block. */
type PdRunLoadState = 'none' | 'loading' | 'ready' | 'error';

/**
 * Read-only processing panel of a {@code pd.PdPartner}. The host passes the partner; on the first load and on every
 * new partner object (refresh after an action or a save) the panel reloads the last collection and export run through
 * the generic CRUD read of their entities, because a run that was RUNNING a moment ago may have finished meanwhile.
 *
 * A run that is reloaded under the same id stays visible while the request is in flight — only a run the panel has
 * not shown yet starts with a skeleton. The runs are read with {@code getLight} (no form metadata). A failed read
 * shows an inline error AND the global error toast: {@code MvsCrudService.get}/{@code getLight} offer no per-call
 * opt-out of the HttpErrorInterceptor.
 */
@Component({
    selector: 'mvs-pd-partner-processing-status',
    templateUrl: './pd-partner-processing-status.component.html',
    styleUrls: ['./pd-partner-processing-status.component.scss'],
    standalone: false
})
export class PdPartnerProcessingStatusComponent implements OnInit, OnChanges, OnDestroy {

    private static readonly OBJECT_TYPE_COLLECTION_RUN = 'pd.PdPartnerCollectionRun';
    private static readonly OBJECT_TYPE_EXPORT_RUN = 'pd.PdPartnerExportRun';

    /** The partner whose lock and last runs are shown. */
    @Input() partner: PdPartnerDto;

    collectionRun: PdPartnerCollectionRunDto | null = null;
    collectionRunState: PdRunLoadState = 'none';

    exportRun: PdPartnerExportRunDto | null = null;
    exportRunState: PdRunLoadState = 'none';

    initialized: boolean = false;

    private collectionRunSubscription: Subscription | null = null;
    private exportRunSubscription: Subscription | null = null;

    constructor(
        private coreService: MvsCoreService,
        private navigationService: MvsObjectNavigationService,
        @Inject(LOCALE_ID) private locale: string
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /** A new partner object (refresh after an action or a save) may carry finished runs — reload them. */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['partner']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.collectionRunSubscription?.unsubscribe();
        this.exportRunSubscription?.unsubscribe();
    }

    // =================================================================================================================
    // Lock
    // =================================================================================================================

    get isLocked(): boolean {
        return pdIsLocked(this.partner?.processingStatus);
    }

    get processingStatusLabel(): string {
        return pdProcessingStatusLabel(this.partner?.processingStatus);
    }

    get processingStatusChipClass(): string {
        return pdStatusChipClass(pdProcessingStatusTone(this.partner?.processingStatus));
    }

    /** Which job holds the lock; empty without a lock (the server clears it on release). */
    get processingKindLabel(): string {
        return pdProcessingKindLabel(this.partner?.processingKind);
    }

    // =================================================================================================================
    // Labels and chips (status chips are PrimeFlex-token spans, see pd-status-display.ts)
    // =================================================================================================================

    runStatusLabel(status: PdRunStatusEnum | null | undefined): string {
        return pdRunStatusLabel(status);
    }

    runStatusChipClass(status: PdRunStatusEnum | null | undefined): string {
        return pdStatusChipClass(pdRunStatusTone(status));
    }

    /** Chip classes of a plain attribute (mode, channel, dry run). */
    chipClass(tone: PdStatusTone): string {
        return pdStatusChipClass(tone);
    }

    collectionModeLabel(run: PdPartnerCollectionRunDto): string {
        return pdCollectionModeLabel(run?.mode);
    }

    exportChannelLabel(run: PdPartnerExportRunDto): string {
        return pdExportChannelLabel(run?.channel);
    }

    /** An Instant as DD.MM.YYYY HH:mm in local time; '' when empty. */
    formatDateTime(value: unknown): string {
        return pdFormatDateTime(value, this.locale);
    }

    // =================================================================================================================
    // Navigation into the generic object view
    // =================================================================================================================

    openCollectionRun(): void {
        this.openObject(PdPartnerProcessingStatusComponent.OBJECT_TYPE_COLLECTION_RUN, this.collectionRun?.id);
    }

    openExportRun(): void {
        this.openObject(PdPartnerProcessingStatusComponent.OBJECT_TYPE_EXPORT_RUN, this.exportRun?.id);
    }

    private openObject(objectType: string, objectId: number | undefined): void {
        if (!objectId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            objectType, objectId, 'object', '', '', '', MvsObjectNavigationActionEnum.display);
        this.navigationService.navigateTo(entry, 'right');
    }

    // =================================================================================================================
    // Loading
    // =================================================================================================================

    private initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    /** (Re)loads the last collection run and the last export run of the partner. */
    private refreshComponent(): void {
        this.collectionRunSubscription?.unsubscribe();
        this.collectionRunSubscription = this.loadRun<PdPartnerCollectionRunDto>(
            PdPartnerProcessingStatusComponent.OBJECT_TYPE_COLLECTION_RUN,
            this.partner?.lastCollectionRunDtoId,
            this.collectionRun,
            (state, run) => {
                this.collectionRunState = state;
                this.collectionRun = run;
            });

        this.exportRunSubscription?.unsubscribe();
        this.exportRunSubscription = this.loadRun<PdPartnerExportRunDto>(
            PdPartnerProcessingStatusComponent.OBJECT_TYPE_EXPORT_RUN,
            this.partner?.lastExportRunDtoId,
            this.exportRun,
            (state, run) => {
                this.exportRunState = state;
                this.exportRun = run;
            });
    }

    /**
     * Loads one run through the generic light CRUD read of its entity (registered route-only in the entity-provider).
     *
     * @param current the run shown now — kept on screen while the same id is reloaded
     * @param apply   receives the new state and run
     */
    private loadRun<T extends DtoDetail>(
        objectType: string,
        runId: number | null | undefined,
        current: T | null,
        apply: (state: PdRunLoadState, run: T | null) => void
    ): Subscription | null {
        if (!runId) {
            apply('none', null);
            return null;
        }
        const service = this.coreService.getCrudService(objectType);
        if (!service) {
            apply('error', null);
            return null;
        }
        if (current?.id !== runId) {
            apply('loading', null);
        }
        return service.getLight(runId).subscribe({
            next: run => apply('ready', run as T),
            error: () => apply('error', null)
        });
    }
}
